/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { TextField, Autocomplete } from '@mui/material';
import { openNotification } from 'tt-ui-lib/core';
import { ZoomInIcon } from 'tt-ui-lib/icons';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import clsx from 'clsx';
import { useLazyQuery, useMutation } from '@apollo/client';
import { UPDATE_USER_PROFILE, GET_LANGUAGES_LIST } from 'api';
import AboutTag from '../AboutTag';
import styles from './AboutMyselfEditing.module.scss';
import { useSelectedContext } from 'context/contextSelector';

export const LANGUAGE_LEVELS = [
  { key: 'beginner', name: 'Beginner' },
  { key: 'elementary', name: 'Elementary' },
  { key: 'intermediate', name: 'Intermediate' },
  { key: 'upperintermediate', name: 'Upper-Intermediate' },
  { key: 'advanced', name: 'Advanced' },
  { key: 'proficiency', name: 'Proficiency' },
];

export const AboutMyselfEditing = ({ userProps, setIsEditing, changeIsLoading, setUserProps }) => {
  const { changeUserData, user } = useSelectedContext((context) => context.user);

  const [interest, setInterest] = useState(null);
  const [interests, setInterests] = useState([]);
  const [expertise, setExpertise] = useState(null);
  const [expertises, setExpertises] = useState([]);
  const [languagesList, setLanguagesList] = useState([]);
  const [language, setLanguage] = useState(null);
  const [level, setLevel] = useState(null);
  const [errorExpertise, setErrorExpertise] = useState(false);
  const [errorInterest, setErrorInterest] = useState(false);
  const [errorLanguage, setErrorLanguage] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [interestLong, setInterestLong] = useState(false);

  const [getLanguages] = useLazyQuery(GET_LANGUAGES_LIST);

  const getLanguagesList = async () => {
    const res = await getLanguages();
    if (res.data && res.data.languages) {
      setLanguagesList(res.data.languages);
    }
  };

  useEffect(() => {
    getLanguagesList();
  }, []);

  const formOptions = {
    defaultValues: {
      about: userProps.about,
      interests: [],
      languages: [],
      expertises: [],
    },
  };

  const formMethods = useForm(formOptions, { mode: 'all' });
  const [updateUserProfile] = useMutation(UPDATE_USER_PROFILE, {
    variables: { input: {} },
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    getValues,
    setError,
    clearErrors,
  } = formMethods;

  useEffect(() => {
    if (userProps && userProps.languages) {
      const languageValues = [];
      const languageDataValues = [];
      userProps.languages.forEach((item) => {
        languageDataValues.push({
          name: item.language.english_name,
          level: LANGUAGE_LEVELS.find((i) => i.key === item.level).name,
        });
        languageValues.push({ language_id: item.language.id, level: item.level });
      });

      setLanguages(languageDataValues);
      setValue('languages', languageValues);
    }

    if (userProps && userProps.interests) {
      const interestsValues = [];
      userProps.interests.forEach((item) => {
        interestsValues.push({ body: item.body });
      });
      setInterests(interestsValues);
      setValue('interests', interestsValues);
    }

    if (userProps.role_name !== 'individual' && userProps && userProps?.expertises) {
      const expertisesValues = [];
      userProps.expertises.forEach((item) => {
        expertisesValues.push({
          name: item.name,
          id: item.id,
        });
      });
      setExpertises(expertisesValues);
      setValue('expertises', expertisesValues);
    }
  }, []);

  const onSubmit = async (data) => {
    const formData = {
      about: data.about,
      interests: data.interests,
      languages: data.languages,
    };

    if (userProps.role_name === 'expert') {
      formData.expertises = data.expertises;
      if (formData.expertises.length > 10) {
        setError('expertises', {
          type: 'custom',
          message: "The list of expertise can't be more than 10 expertise.",
        });
      }
      if (!data.expertises || !data.expertises.length) {
        setError('expertises', {
          type: 'custom',
          message: 'This is a required field.',
        });
        return true;
      }
    }

    try {
      changeIsLoading(true);
      const res = await updateUserProfile({ variables: { input: formData } });
      changeUserData({ ...user, ...res.data.updateUserProfile });
      setUserProps(res.data.updateUserProfile);
      openNotification({
        message: 'About information changed successfully.',
        type: 'success',
      });
      setIsEditing(false);
    } catch (e) {
      openNotification({
        message: 'Error while updating about information.',
        type: 'error',
      });
      console.log('error: ', e);
    }
    changeIsLoading(false);
  };

  const deleteLanguage = (index) => {
    let oldLanguages = getValues('languages');
    oldLanguages = oldLanguages.filter((item, i) => i !== index);
    setValue('languages', [...oldLanguages]);
    setLanguages((current) => current.filter((item, i) => i !== index));
    setErrorLanguage(false);
  };

  const deleteInterest = (index) => {
    let oldInterests = getValues('interests');
    oldInterests = oldInterests.filter((item, i) => i !== index);
    setValue('interests', [...oldInterests]);
    setInterests((current) => current.filter((item, i) => i !== index));
    setErrorInterest(false);
  };

  const deleteExpertise = (index) => {
    let oldExpertises = getValues('expertises');
    oldExpertises = oldExpertises.filter((item, i) => i !== index);
    setValue('expertises', [...oldExpertises]);
    setExpertises((current) => current.filter((item, i) => i !== index));
    setErrorExpertise(false);
  };

  const addInterests = () => {
    const oldInterests = getValues('interests');
    if (interest && interest.length > 30) {
      setInterestLong(true);
      return;
    }
    if (interest) {
      setInterestLong(false);
      const value = {
        body: interest,
      };

      let interestsValidation = true;

      oldInterests.forEach((item) => {
        if (value.body === item.body) {
          interestsValidation = false;
        }
      });

      if (interestsValidation) {
        setInterests((current) => [...current, value]);
        setValue('interests', [...oldInterests, value]);
        setInterest('');
      } else {
        setErrorInterest(true);
      }
    }
  };

  const addExpertise = () => {
    const oldExpertises = getValues('expertises');
    if (expertise) {
      const value = {
        name: expertise,
      };

      let expValidation = true;

      oldExpertises.forEach((item) => {
        if (value.name === item.name) {
          expValidation = false;
        }
      });

      if (expValidation) {
        setExpertises((current) => [...current, value]);
        setValue('expertises', [...oldExpertises, value]);
        clearErrors('expertises');
        setExpertise('');
      } else {
        setErrorExpertise(true);
      }
    }
  };

  const addLanguages = () => {
    const oldLanguages = getValues('languages');
    if (language && level) {
      const value = {
        language_id: language.id,
        level: level.key,
      };
      let languageValidation = true;
      oldLanguages.forEach((item) => {
        if (value.language_id === item.language_id) {
          languageValidation = false;
        }
      });

      if (languageValidation) {
        setLanguages((current) => [...current, { name: language.english_name, level: level.name }]);
        setValue('languages', [...oldLanguages, value]);

        setLevel(null);
        setLanguage(null);
        setErrorLanguage(false);
      } else {
        setErrorLanguage(true);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addInterests();
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form id="aboutForm" onSubmit={handleSubmit(onSubmit)}>
        {userProps?.role_name === 'expert' && (
          <div className={styles.fieldWrapper}>
            <div className={styles.fieldTitle}>Expertises</div>
            <div className={styles.content}>
              <TextField
                sx={{ width: '100%' }}
                error={errorExpertise}
                helperText={errorExpertise && 'This expertise is already exist'}
                className={clsx(styles.modal_input)}
                label="Add your expertises"
                value={expertise}
                onChange={(e) => {
                  setErrorExpertise(false);
                  if (e.target.value && e.target.value.length > 60) {
                    return;
                  }
                  setExpertise(e.target.value);
                }}
                disabled={expertises.length >= 10}
              />
              <button
                className={styles.editButton}
                type="button"
                style={errorExpertise ? { marginBottom: '23px' } : null}
                onClick={() => addExpertise()}
              >
                <ZoomInIcon style={{ width: 24, height: 24 }} />
              </button>
            </div>
            {errors.expertises?.message && (
              <p className={styles.errorMessage}>{errors.expertises?.message}</p>
            )}
            <div className={styles.tagsWrapper}>
              <div className={styles.itemsList}>
                {expertises.map((elem, index) => (
                  <AboutTag
                    key={elem.name}
                    tagText={elem.name}
                    index={index}
                    deleteItem={deleteExpertise}
                    tags={getValues('expertises')}
                    isEditing
                  />
                ))}
              </div>
            </div>
          </div>
        )}
        <div className={styles.fieldTitle}>About</div>
        <Controller
          name="about"
          control={control}
          render={({ field: { value, onChange, ...field } }) => (
            <TextField
              {...field}
              className={clsx(styles.modal_input, styles.textArea)}
              label="About myself"
              value={value}
              onChange={onChange}
              inputProps={{
                maxLength: 2000,
              }}
              minRows={4}
              multiline
            />
          )}
        />
        <div className={styles.fieldWrapper}>
          <div className={styles.fieldTitle}>Interests</div>
          <div className={styles.content}>
            <TextField
              error={errorInterest || interestLong}
              helperText={
                errorInterest
                  ? 'This interest is already exist'
                  : interestLong
                    ? 'This interest is too long'
                    : null
              }
              className={clsx(styles.modal_input)}
              label="Add your interests"
              value={interest}
              onChange={(e) => {
                setErrorInterest(false);
                setInterest(e.target.value);
              }}
              onKeyDown={handleKeyPress}
              disabled={interests.length >= 5}
            />
            <button
              className={styles.editButton}
              type="button"
              style={errorInterest ? { marginBottom: '23px' } : null}
              onClick={() => addInterests()}
            >
              <ZoomInIcon style={{ width: 24, height: 24 }} />
            </button>
          </div>
          <div className={styles.tagsWrapper}>
            <div className={styles.itemsList}>
              {interests.map((elem, index) => (
                <AboutTag
                  key={elem.body}
                  tagText={elem.body}
                  index={index}
                  deleteItem={deleteInterest}
                  tags={getValues('interests')}
                  isEditing
                />
              ))}
            </div>
          </div>
        </div>
        <div>
          <div className={styles.fieldTitle}>Language</div>
          <div>
            <div className={styles.language}>
              <div className={styles.languageWrapper}>
                <Autocomplete
                  id="languages-box"
                  value={language}
                  options={languagesList}
                  getOptionLabel={(option) => option.english_name}
                  sx={{ width: '100%' }}
                  onChange={(e, value) => {
                    setLanguage(value);
                    setErrorLanguage(false);
                  }}
                  disablePortal
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={errorLanguage}
                      helperText={errorLanguage && 'This language is already exist'}
                      className={clsx(styles.modal_input, styles.select)}
                      label="Add language"
                    />
                  )}
                />
                <Autocomplete
                  id="levels-box"
                  value={level}
                  options={LANGUAGE_LEVELS}
                  getOptionLabel={(option) => option.name}
                  sx={{ width: '100%' }}
                  disablePortal
                  onChange={(e, value) => setLevel(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      className={clsx(styles.modal_input, styles.select)}
                      label="Select level"
                    />
                  )}
                />
              </div>
              <button
                className={styles.editButtonT}
                style={errorLanguage ? { marginBottom: '23px' } : null}
                type="button"
                onClick={() => addLanguages()}
              >
                <ZoomInIcon style={{ width: 24, height: 24 }} />
              </button>
            </div>
          </div>
          <div className={styles.tagsWrapper}>
            <div className={styles.itemsList}>
              {languages.map((elem, index) => (
                <AboutTag
                  key={`${elem.name}${elem.level}`}
                  tagText={`${elem.name} - ${elem.level}`}
                  index={index}
                  deleteItem={deleteLanguage}
                  tags={getValues('languages')}
                  isEditing
                />
              ))}
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
